import { html } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';

import { LightLitElement } from '@elab/components';
import { actions as globalActions } from '@elab/core/store/global';
import { actions as registrationAction } from '@elab/components/connected/forms/RegistrationForm/redux/index';
import {
    getSelectors as checkoutFormsSelectors,
    createActions as checkoutRegistrationAction,
} from '@elab/components/connected/forms/CheckoutForms/redux';
import {
    actions as analyticsActions,
    selectors as analyticsSelectors,
} from '@elab/core/store/analytics';
import connect from '@elab/core/store/connect';
import { selectors as registrationFormSelectors } from '@elab/components/connected/forms/RegistrationForm/redux';
import { selectors as userSelectors } from '@elab/core/store/user';
import storeNames from '@elab/components/connected/forms/CheckoutForms/storeNames';
import { camelCaseToKebabCase } from '@elab/core/utils/helpers';

import { selectors as deliveryModeSelectors } from './DeliveryModes/redux';

/*
    QUICK GUIDE
    - name = identifier of breadcrumb item
    - url = redirect url, if not passed stepNumber will be used
    - disabled = well, you got it
    - stepNumber = position of this item, used in case of click on registration form to change step and to decide if it's enabled or not

    Parent.name (BreadcrumbsCheckout) is used during checkout to inform children of the page we are in so that, each of them can decide what to do
*/
export class BreadCrumbsItem extends LightLitElement {
    static get properties() {
        return {
            classModifier: { type: String, attribute: 'class-modifier' },
            name: { type: String },
            url: { type: String },
            disabled: { type: Boolean },
            stepNumber: { type: Number, attribute: 'step-number' },
            itemLabel: { type: String, attribute: 'item-label' },
            dataRequired: { type: Boolean },
            showPrefix: { type: Boolean, attribute: 'show-prefix' },
            emptyPrefixForSucceed: {
                type: Boolean,
                attribute: 'empty-prefix-for-succeed',
            },
            active: { type: Boolean, attribute: 'is-active', reflect: true },
            isLast: { type: Boolean, attribute: 'is-last' },
            isOnCheckoutRegistration: {
                type: Boolean,
                attribute: 'is-on-checkout-registration',
            },
            tickSuccessfulSteps: {
                type: Boolean,
                attribute: 'tick-successful-steps',
            },
            isOnCheckout: {
                type: Boolean,
                attribute: 'is-on-checkout',
            },
            isLogged: { type: Boolean, attribute: false },
            isRegisteredOnCheckout: { type: Boolean, attribute: false },
            conditionStep: { type: Number, attribute: false },
            checkoutConditionStep: { type: Number, attribute: false },
            deliveryBlocked: { type: Boolean, attribute: false },
            closestSelectorClass: { type: String, attribute: 'closest-selector-class' },
        };
    }

    constructor() {
        super();
        this.name = '';
        this.itemLabel = '';
        this.url = '';
        this.disabled = false;
        this.dataRequired = false;
        this.showPrefix = false;
        this.emptyPrefixForSucceed = false;
        this.isLast = false;
        this.isRegisteredOnCheckout = false;
        this.classModifier = '';
        this.active = false;
        this.isOnCheckoutRegistration = false;
        this.tickSuccessfulSteps = false;
        this.isLogged = false;
        this.conditionStep = 0;
        this.checkoutConditionStep = 0;
        this.deliveryBlocked = false;
        this.isOnCheckout = false;
        this.closestSelectorClass = '';
        this.lastStepNumber = 0;
    }

    firstUpdated() {
        if (
            this.stepNumber &&
            (this.stepNumber === 1 || this.stepNumber !== this.lastStepNumber) &&
            this.isActive &&
            this.isOnCheckout
        ) {
            this.dispatchEvent(
                new CustomEvent('on-step', {
                    detail: { step: this.stepNumber },
                }),
            );
        }
    }

    handleClick() {
        if (this.isAvailableForNavigation) {
            if (this.url) {
                this.dispatchEvent(
                    new CustomEvent('on-click', {
                        detail: {
                            url: this.url,
                        },
                    }),
                );
            } else if (this.isOnCheckoutRegistration && this.checkoutConditionStep) {
                this.dispatchEvent(
                    new CustomEvent('on-checkout-registration-step-click', {
                        detail: {
                            stepNumber: this.stepNumber,
                        },
                    }),
                );
            } else if (this.stepNumber) {
                this.dispatchEvent(
                    new CustomEvent('on-step-click', {
                        detail: {
                            stepNumber: this.stepNumber,
                        },
                    }),
                );
            }
        }
    }

    renderPrefix() {
        const prefixValue = !(this.emptyPrefixForSucceed && this.isAvailableForNavigation)
            ? this.stepNumber
            : '';

        const prefixClasses = classMap({
            'e2-breadcrumbs__prefix': true,
            'e2-breadcrumbs__prefix--tick':
                this.emptyPrefixForSucceed &&
                this.isAvailableForNavigation &&
                this.tickSuccessfulSteps,
        });

        return this.showPrefix
            ? html`
                  <span class="${prefixClasses}">${prefixValue}</span>
              `
            : '';
    }

    get closestBreadcrumbs() {
        if (!this.closestSelectorClass) return this.closest('e2-breadcrumbs');
        const proxiHendler = {
            get(target, prop) {
                if (!target) {
                    return null;
                }
                return target.getAttribute(camelCaseToKebabCase(prop));
            },

            set(target, prop, value) {
                if (!target) {
                    return false;
                }
                target.setAttribute(camelCaseToKebabCase(prop), value);
                return true;
            },
        };
        return new Proxy(this.closest(`.${this.closestSelectorClass}`), proxiHendler);
    }

    get isClickForward() {
        if (this.closestSelectorClass && this.closestBreadcrumbs.stepNumber === null) {
            return this.stepNumber > this.conditionStep;
        }
        return (
            this.stepNumber > this.closestBreadcrumbs.stepNumber ||
            this.stepNumber > this.conditionStep
        );
    }

    get isDisabled() {
        return (
            this.disabled ||
            this.deliveryBlocked ||
            (this.isClickForward && !this.closestBreadcrumbs.twoWayNavigation) ||
            (this.isOnCheckoutRegistration && this.stepNumber > this.checkoutConditionStep) ||
            (this.dataRequired && !this.isActive && this.isLogged && !this.isRegisteredOnCheckout)
        );
    }

    get isActive() {
        return (
            this.stepNumber === this.closestBreadcrumbs.stepNumber ||
            this.stepNumber === this.conditionStep ||
            this.name === this.closestBreadcrumbs.name ||
            (this.isOnCheckoutRegistration && this.stepNumber === this.checkoutConditionStep)
        );
    }

    get isAvailableForNavigation() {
        return !this.isActive && !this.isDisabled;
    }

    updated(_changedProperties) {
        super.updated(_changedProperties);

        if (this.name === this.closestBreadcrumbs.name) {
            this.closestBreadcrumbs.stepNumber = this.stepNumber;
        }

        this.active = this.isActive;
    }

    render() {
        const containerClass = classMap({
            'e2-breadcrumbs': true,
            'e2-breadcrumbs--last': this.isLast,
            'e2-breadcrumbs--current': this.isActive,
            [`e2-breadcrumbs--${this.classModifier}`]: this.classModifier,
            'e2-breadcrumbs--disabled': this.isDisabled,
        });

        const isCartBreadcrumb = this.classModifier.includes('cart-page');

        const itemClass = classMap({
            'e2-breadcrumbs__item': true,
            'e2-breadcrumbs__item--succeed': this.isAvailableForNavigation && !this.isClickForward,
            'e2-breadcrumbs__item--current':
                this.name === this.closestBreadcrumbs.name ||
                String(this.name) === String(this.conditionStep) ||
                (this.isOnCheckoutRegistration &&
                    String(this.name) === String(this.checkoutConditionStep)),
            'e2-breadcrumbs__item--disabled': this.isDisabled,
            'e2-breadcrumbs__item--cart-page': isCartBreadcrumb,
        });

        const itemTextClass = classMap({
            'e2-breadcrumbs__item--succeed--text': true,
            'e2-breadcrumbs__item--content': true,
        });

        const tabIndex = this.isAvailableForNavigation ? '0' : '-1';

        return html`
            <div class="${containerClass}">
                <ul class="e2-breadcrumbs__items">
                    <li @click=${this.handleClick} class="${itemClass}">
                        ${this.renderPrefix()}
                        <span tabIndex=${tabIndex} class="${itemTextClass}">
                            ${this.itemLabel || null}
                            <v-slot
                                class="${!this.itemLabel ? 'd-contents' : 'd-none'}"
                                name="icon"
                            ></v-slot>
                        </span>
                    </li>
                </ul>
            </div>
        `;
    }
}

const mapStateToProps = state => ({
    conditionStep: registrationFormSelectors.getStep(state),
    checkoutConditionStep: checkoutFormsSelectors(storeNames.CHECKOUT_REGISTRATION).getStep
        ? checkoutFormsSelectors(storeNames.CHECKOUT_REGISTRATION).getStep(state)
        : 0,
    isLogged: userSelectors.getUserId(state),
    isRegisteredOnCheckout: userSelectors.isRegisteredOnCheckout(state),
    deliveryBlocked: deliveryModeSelectors.getDeliveryBlocked(state),
    lastStepNumber: analyticsSelectors.getCurrentStep(state),
});

const mapDispatchToEvents = dispatch => {
    return {
        'on-click': ({ detail }) => {
            dispatch(globalActions.redirect(detail.url));
        },
        'on-step-click': ({ detail }) => {
            dispatch(registrationAction.setStep(detail.stepNumber));
        },
        'on-checkout-registration-step-click': ({ detail }) => {
            dispatch(
                checkoutRegistrationAction(storeNames.CHECKOUT_REGISTRATION).setStep(
                    detail.stepNumber,
                ),
            );
        },
        'on-step': ({ detail }) => dispatch(analyticsActions.setCurrentStep(detail.step)),
    };
};

export default () =>
    customElements.define(
        'e2-breadcrumbs-item',
        connect(mapStateToProps, mapDispatchToEvents)(BreadCrumbsItem),
    );
